/* Hydra is priced by Shopify Functions. Never calculate a discounted cart total here. */
(() => {
  'use strict';
  window.createPazeHydraCart = (config) => {
    const root = window.Shopify?.routes?.root || '/';
    const codes = ['PAZE-HYDRA', 'PAZE-HYDRA-FLASK'];
    const retiredCodes = ['PAZE-BUY2GET1', 'PAZE-DUO', 'PAZE-DUO-LOOP-OLD', 'PAZE-DUO-FLASKS'];
    const isLoop = (item) => Number(item.product_id) === Number(config.loopProductId);
    const isFlask = (item) => Number(item.variant_id) === Number(config.giftVariantId);
    const role = (item) => item.properties?._paze_duo_role || '';
    const isGift = (item) => isFlask(item) && role(item) === 'gift';
    const loopCount = (cart) => cart.items.filter(isLoop).reduce((sum, item) => sum + item.quantity, 0);
    const strip = (properties = {}) => Object.fromEntries(Object.entries(properties).map(([key, value]) => [key, key.startsWith('_paze_duo') ? '' : value]));
    const request = async (path, data) => {
      const response = await fetch(root + path, data === undefined
        ? { headers: { Accept: 'application/json' }, cache: 'no-store' }
        : { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Cart update failed');
      return result;
    };
    const get = () => request('cart.js');
    const change = (item, quantity, properties) => request('cart/change.js', {
      id: item.key, quantity, ...(properties ? { properties } : {})
    });
    const setProperties = (item, properties) => change(item, item.quantity, properties);

    const normalize = async () => {
      let cart = await get();
      const eligible = loopCount(cart) >= 2;
      if (!eligible) {
        // Only automatically supplied gifts disappear. Paid accessories are preserved.
        while (cart.items.some(isGift)) {
          cart = await change(cart.items.find(isGift), 0);
        }
        while (cart.items.some((item) => isLoop(item) && item.properties?._paze_duo_offer)) {
          const item = cart.items.find((line) => isLoop(line) && line.properties?._paze_duo_offer);
          cart = await setProperties(item, strip(item.properties));
        }
      } else {
        // The benefit is capped at one discounted Loop and one Flask per order.
        if (!cart.items.some((item) => isLoop(item) && role(item) === 'loop_2')) {
          const loops = cart.items.filter(isLoop);
          const item = loops[loops.length - 1];
          cart = await setProperties(item, {
            ...strip(item.properties), _paze_duo_offer: 'paze-hydra-cart', _paze_duo_role: 'loop_2'
          });
        }
        if (!cart.items.some(isGift)) {
          const paidFlask = cart.items.find(isFlask);
          if (paidFlask?.quantity === 1) {
            cart = await setProperties(paidFlask, {
              ...paidFlask.properties, _paze_duo_offer: 'paze-hydra-cart', _paze_duo_role: 'gift', _paze_duo_gift: 'soft_flask'
            });
          } else {
            // Add first, then remove the converted paid unit. Never lose a paid item if adding fails.
            await request('cart/add.js', { items: [{
              id: Number(config.giftVariantId), quantity: 1,
              properties: { _paze_duo_offer: 'paze-hydra-cart', _paze_duo_role: 'gift', _paze_duo_gift: 'soft_flask' }
            }] });
            cart = await get();
            if (paidFlask) {
              const line = cart.items.find((item) => isFlask(item) && !isGift(item) && item.key === paidFlask.key)
                || cart.items.find((item) => isFlask(item) && !isGift(item));
              if (line) cart = await change(line, line.quantity - 1);
            }
          }
        }
        // Legacy carts or repeated bundle additions must not accumulate free gifts.
        while (cart.items.filter(isGift).reduce((sum, item) => sum + item.quantity, 0) > 1) {
          const gifts = cart.items.filter(isGift);
          const item = gifts[gifts.length - 1];
          cart = await change(item, gifts.length === 1 ? 1 : 0);
        }
      }
      const otherCodes = (cart.discount_codes || []).map((entry) => entry.code).filter((code) => {
        const upper = code.toUpperCase();
        return !codes.includes(upper) && !retiredCodes.includes(upper);
      });
      const desired = [...otherCodes, ...(eligible ? codes : [])];
      const current = (cart.discount_codes || []).map((entry) => entry.code.toUpperCase()).sort().join(',');
      if (current !== desired.map((code) => code.toUpperCase()).sort().join(',')) {
        cart = await request('cart/update.js', { discount: desired.join(',') });
      }
      // Shopify can allocate the free unit to a newly added Flask. Mark that actual unit
      // as the gift, keeping any additional, paid Flasks editable and removable.
      cart = await get();
      const freeFlask = cart.items.find((item) => isFlask(item) && item.quantity === 1
        && Number(item.final_line_price) === 0 && (item.line_level_discount_allocations || [])
          .some((entry) => entry.discount_application?.title === 'PAZE-HYDRA-FLASK'));
      if (eligible && freeFlask && !isGift(freeFlask)) {
        const previousGift = cart.items.find(isGift);
        if (previousGift) cart = await setProperties(previousGift, strip(previousGift.properties));
        const actualFreeFlask = cart.items.find((item) => isFlask(item) && item.quantity === 1 && Number(item.final_line_price) === 0);
        if (actualFreeFlask) await setProperties(actualFreeFlask, {
          ...actualFreeFlask.properties, _paze_duo_offer: 'paze-hydra-cart', _paze_duo_role: 'gift', _paze_duo_gift: 'soft_flask'
        });
      }
      // After an existing checkout changes, a mutation response can include its shipping rate.
      // Read the canonical merchandise subtotal again before rendering the drawer.
      return get();
    };

    let queue = Promise.resolve();
    let pending = 0;
    const run = (operation) => {
      pending += 1;
      document.dispatchEvent(new CustomEvent('paze:cart-busy', { detail: { busy: true } }));
      const task = queue.then(async () => {
        if (operation) await operation({ get, request, change, isLoop, loopCount });
        const cart = await normalize();
        document.dispatchEvent(new CustomEvent('paze:cart-change', { detail: { cart } }));
        return cart;
      });
      queue = task.catch(() => {}).finally(() => {
        pending -= 1;
        document.dispatchEvent(new CustomEvent('paze:cart-busy', { detail: { busy: pending > 0 } }));
      });
      return task;
    };
    return {
      run,
      refresh: () => run(),
      add: (items) => run(async ({ get, request, loopCount }) => {
        const benefitUsed = loopCount(await get()) >= 2;
        const nextItems = items.map((item) => benefitUsed && item.properties?._paze_duo_role === 'loop_2'
          ? { ...item, properties: { ...item.properties, _paze_duo_role: 'loop_extra' } }
          : item);
        return request('cart/add.js', { items: nextItems });
      }),
      change: (key, quantity) => run(({ request }) => request('cart/change.js', { id: key, quantity })),
      get busy() { return pending > 0; }
    };
  };
})();
